import React, { useEffect, useRef, useState } from "react";
import { DOCUMENT_ACCEPT_STRING } from "shared/constants/officeFiles";
import { useEditor, EditorContent } from "@tiptap/react";
import { Icons } from "assets";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import styles from "./style.module.scss";
import classNames from "classnames";

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  customContainerStyle?: string;
  customToolbarStyle?: string;
  onFileSelect?: (files: File[]) => void;
  onSend?: () => void;
  selectedFiles?: File[];
  onRemoveFile?: (index: number) => void;
  isSubmitting?: boolean;
  showFileUpload?: boolean;
  showSendButton?: boolean;
  fileInputId?: string;
  error?: string;
  onErrorChange?: (error: string) => void;
  disabled?: boolean;
}

const CustomTextEditor = ({
  value = "",
  label,
  required = false,
  onChange,
  placeholder = "Enter text here...",
  customContainerStyle,
  customToolbarStyle,
  onFileSelect,
  onSend,
  selectedFiles = [],
  onRemoveFile,
  isSubmitting = false,
  showFileUpload = false,
  showSendButton = false,
  fileInputId = "file-input",
  error,
  onErrorChange,
  disabled = false,
}: RichTextEditorProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string>("");

  const displayError = error !== undefined ? error : internalError;
  const setError = onErrorChange || setInternalError;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-link",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
      if (internalError || error) {
        setError("");
      }
    },
    editorProps: {
      attributes: {
        class: styles.editorContent,
        "data-placeholder": placeholder,
        style: `--placeholder: "${placeholder}"`,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    const updateEmptyClass = () => {
      const editorElement = editor.view.dom as HTMLElement;

      const textContent = editor.getText().trim();
      const doc = editor.state.doc;
      const onlyEmptyParagraph =
        doc.childCount === 1 &&
        doc.firstChild?.type.name === "paragraph" &&
        doc.firstChild.childCount === 0;
      const isReallyEmpty = !textContent && onlyEmptyParagraph;

      if (isReallyEmpty) {
        editorElement.classList.add("is-empty");
        editorElement.setAttribute("data-empty", "true");
      } else {
        editorElement.classList.remove("is-empty");
        editorElement.removeAttribute("data-empty");
      }
    };

    setTimeout(updateEmptyClass, 0);

    editor.on("update", updateEmptyClass);
    editor.on("create", updateEmptyClass);
    editor.on("selectionUpdate", updateEmptyClass);

    return () => {
      editor.off("update", updateEmptyClass);
      editor.off("create", updateEmptyClass);
      editor.off("selectionUpdate", updateEmptyClass);
    };
  }, [editor]);

  const setLink = () => {
    const previousUrl = editor?.getAttributes("link").href;
    const url = window.prompt("Enter URL:", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    const { from, to } = editor!.state.selection;
    const hasSelection = from !== to;
    const isExistingLink = !!previousUrl;

    if (hasSelection || isExistingLink) {
      editor
        ?.chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    } else {
      editor
        ?.chain()
        .focus()
        .insertContent(`<a href="${url}">${url}</a>`)
        .run();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && onFileSelect) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter((file) => {
        const fileType = file.type;
        return (
          fileType.startsWith("image/") ||
          fileType === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf")
        );
      });
      onFileSelect(validFiles);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAttachClick = () => {
    if (showFileUpload && fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      setLink();
    }
  };

  const stripHtmlTags = (html: string): string => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const handleSendClick = () => {
    if (!onSend) return;

    const plainText = stripHtmlTags(value);

    if (!plainText.trim()) {
      setError("Please enter a message");
      return;
    }

    setError("");
    onSend();
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/")) {
      return "image";
    }
    return "pdf";
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={styles.editorWrapper}>
      {label && (
        <label className={styles.editorLabel}>
          {label}
          {required && <span className="asterik">*</span>}
        </label>
      )}
      {selectedFiles.length > 0 && (
        <div
          className={classNames(
            "flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md mb-2",
          )}
        >
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-white px-3 py-1 rounded border border-gray-200"
            >
              {getFileIcon(file) === "image" ? (
                <Icons.TabImage className="w-4 h-4" />
              ) : (
                <Icons.TabOverview className="w-4 h-4" />
              )}
              <span className="text-sm text-gray-700 max-w-[150px] truncate">
                {file.name}
              </span>
              {onRemoveFile && (
                <button
                  type="button"
                  onClick={() => onRemoveFile(index)}
                  className="text-red-500 hover:text-red-700 ml-1"
                >
                  <Icons.Cross className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div
        className={classNames(
          styles.editorContainer,
          customContainerStyle,
          displayError && styles.hasError,
          disabled && styles.disabled,
        )}
      >
        <EditorContent editor={editor} className={styles.editorArea} />
        <div className={classNames(styles.toolbar, customToolbarStyle)}>
          {showFileUpload ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={DOCUMENT_ACCEPT_STRING}
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id={fileInputId}
              />
              <button
                type="button"
                onClick={handleAttachClick}
                className={styles.toolbarButton}
                title="Attach file (images or PDFs)"
                disabled={disabled}
              >
                <Icons.AttachIcon />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={setLink}
              className={`${styles.toolbarButton} ${
                editor.isActive("link") ? styles.active : ""
              }`}
              title="Link"
            >
              <Icons.AttachIcon />
            </button>
          )}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`${styles.toolbarButton} ${
              editor.isActive("bold") ? styles.active : ""
            }`}
            title="Bold"
          >
            <Icons.MonotonIcon6 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`${styles.toolbarButton} ${
              editor.isActive("italic") ? styles.active : ""
            }`}
            title="Italic"
          >
            <Icons.MonotonIcon7 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`${styles.toolbarButton} ${
              editor.isActive("underline") ? styles.active : ""
            }`}
            title="Underline"
          >
            <Icons.MonotonIcon8 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`${styles.toolbarButton} ${
              editor.isActive("strike") ? styles.active : ""
            }`}
            title="Strikethrough"
          >
            <Icons.MonotonIcon />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            className={`${styles.toolbarButton} ${styles.flipIcon} ${
              editor.isActive({ textAlign: "left" }) ? styles.active : ""
            }`}
            title="Align Left"
          >
            <Icons.MonotonIcon1 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            className={`${styles.toolbarButton} ${
              editor.isActive({ textAlign: "center" }) ? styles.active : ""
            }`}
            title="Align Center"
          >
            <Icons.MonotonIcon2 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            className={`${styles.toolbarButton} ${
              editor.isActive({ textAlign: "right" }) ? styles.active : ""
            }`}
            title="Align Right"
          >
            <Icons.MonotonIcon3 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`${styles.toolbarButton} ${
              editor.isActive("orderedList") ? styles.active : ""
            }`}
            title="Ordered List"
          >
            <Icons.MonotonIcon4 />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`${styles.toolbarButton} ${
              editor.isActive("bulletList") ? styles.active : ""
            }`}
            title="Bullet List"
          >
            <Icons.MonotonIcon5 />
          </button>
          {showSendButton && (
            <button
              type="button"
              onClick={handleSendClick}
              disabled={isSubmitting || disabled}
              className={classNames(
                styles.sendButton,
                "ml-auto",
                (isSubmitting || disabled) && "opacity-50 cursor-not-allowed",
              )}
              title={disabled ? "Chat is read-only" : "Send"}
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icons.SendIcon />
              )}
            </button>
          )}
        </div>
      </div>

      {displayError && (
        <div className={styles.errorMessage}>{displayError}</div>
      )}
    </div>
  );
};

export default CustomTextEditor;
